import { Navigate, useLocation } from 'react-router-dom';
import { useOptionalAuth } from './AuthProvider';
import React from 'react';
import { useCurrentSubscription } from '@/hooks/useCurrentSubscription';
import { useEntitlementsSettled } from '@/hooks/useEntitlementsSettled';
import { ShellLoadingScreen } from '@/components/layout/ShellLoadingScreen';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const auth = useOptionalAuth();
  const user = auth?.user ?? null;
  const loading = auth?.loading ?? true;
  const profileSynced = auth?.profileSynced ?? false;
  const location = useLocation();

  const isSuperAdmin = !!(user as any)?.is_super_admin;
  const isAgency = !!(user as any)?.is_agency;
  const isRegularUser = !!user && !isSuperAdmin && !isAgency;
  const organizationId = (user as any)?.organization_id;

  const { data: subscriptionData, isLoading: subscriptionLoading, isError: subscriptionError } =
    useCurrentSubscription({
      enabled: !loading && isRegularUser && !!organizationId,
    });

  const isBootstrappingEntitlements =
    isRegularUser && subscriptionLoading && subscriptionData === undefined && !subscriptionError;

  const entitlementsSettled = useEntitlementsSettled(isRegularUser && !!organizationId);

  const profileSyncing = profileSynced === false;
  const isActuallyLoading =
    loading ||
    profileSyncing ||
    isBootstrappingEntitlements ||
    (isRegularUser && !!organizationId && !entitlementsSettled);

  const MIN_VISIBLE_MS = 450;
  const everLoadedRef = React.useRef(isActuallyLoading);
  const shownAtRef = React.useRef<number | null>(isActuallyLoading ? Date.now() : null);
  const [holdOpen, setHoldOpen] = React.useState(false);
  React.useEffect(() => {
    if (isActuallyLoading) {
      everLoadedRef.current = true;
      if (shownAtRef.current === null) shownAtRef.current = Date.now();
      setHoldOpen(true);
      return;
    }
    if (!everLoadedRef.current) {
      setHoldOpen(false);
      return;
    }
    const shownFor = shownAtRef.current === null ? 0 : Date.now() - shownAtRef.current;
    const remaining = MIN_VISIBLE_MS - shownFor;
    const release = () => {
      shownAtRef.current = null;
      setHoldOpen(false);
    };
    if (remaining <= 0) {
      release();
      return;
    }
    const t = setTimeout(release, remaining);
    return () => clearTimeout(t);
  }, [isActuallyLoading]);

  if (isActuallyLoading || holdOpen) {
    return (
      <ShellLoadingScreen
        search={location.search}
        showWorkspaceChrome={!isSuperAdmin && !isAgency}
      />
    );
  }

  if (!user) return <Navigate to="/auth" replace />;

  const isAppRoute = location.pathname.startsWith('/home/');

  if (isSuperAdmin && isAppRoute) {
    return <Navigate to="/admin/dashboard" replace />;
  }

  return <>{children}</>;
}
